import store from '@/store';
import { getAccessTokenSilentlyWithMonitoring } from '@/utils/auth0';

/**
 * Guarantees `store.state.user.token` is populated before a route guard makes
 * an authenticated API call, returning the token it settled on.
 *
 * Route guards run inside `router.isReady()` (src/main.js), which resolves the
 * initial navigation *before* the launch-time `getAccessTokenSilently()` commits
 * a token to the store. Vuex is plain in-memory state — nothing rehydrates it on
 * a page load — so `user.token` is the empty string for the whole guard phase of
 * a hard load or deep link.
 *
 * Any guard that calls the API in that window therefore sends `Bearer ` (empty),
 * which the auth-header interceptor strips, which the API answers with 401, which
 * the 401 interceptor used to turn into a full logout. That is exactly what
 * happened to Courses the moment its feature flag was switched on in production:
 * `requireFeatureFlag` had been priming the token as a side effect of its
 * disabled-flag path, and enabling the flag made it return before ever getting
 * there. Priming is now explicit rather than a side effect of a branch that only
 * runs while a feature is off.
 *
 * Never throws: a guard's job is to decide on navigation, not to fail it because
 * Auth0 was slow. Callers that need to know treat a falsy return as "no token".
 *
 * @param {string} source - Label for the Auth0 token monitoring log.
 * @returns {Promise<string|undefined>} The access token, or undefined.
 */
export async function ensureAccessToken(source = 'route-guard') {
  const existing = store.state.user?.token;
  if (existing) return existing;

  // Local dev signs in against localauth0 and seeds the token from localStorage
  // at mount (src/main.js); there is no silent-auth session to ask.
  if (process.env.VUE_APP_NODE_ENV === 'local') return existing;

  try {
    const token = await getAccessTokenSilentlyWithMonitoring({ cacheMode: 'on' }, source);
    if (token) store.commit('user/setToken', token);
    return token;
  } catch (error) {
    console.warn('[Auth] Failed to prime route-guard token:', error?.message);
    return undefined;
  }
}
